import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import api from 'api/api'
import { ACTION_HISTORY_QUERY_POLICY } from 'api/queryPolicies'
import useStore from 'state/knovStore'

export const STARRED_SIDEBAR_QUERY_KEY = ['starredSidebarQuests'] as const

// Slim rows are cheap to serve, so pages are larger than the stream default.
const PER_PAGE = 50

const starredSidebarQueryKey = (spaceId: string | null) => [...STARRED_SIDEBAR_QUERY_KEY, spaceId]

type ResponseData = {
    // Slim sidebar previews (id, content, flags, parent.user), NOT full
    // payloads — do not seed the quest cache with them.
    quests: any[]
    has_more?: boolean
}

export default function useGetStarredSidebarQuests(enabled: boolean = true) {
    const activeSpaceId = useStore(state => state.activeSpaceId)
    const shouldFetch = enabled && Boolean(activeSpaceId)

    const result = useInfiniteQuery({
        queryKey: starredSidebarQueryKey(activeSpaceId),
        queryFn: async ({ pageParam = 1 }) => {
            const page = typeof pageParam === 'number' ? pageParam : Number(pageParam) || 1
            const data: ResponseData = await api.getStarredSidebarQuests(activeSpaceId, {
                page,
                per_page: PER_PAGE,
            })
            return data
        },
        enabled: shouldFetch,
        staleTime: ACTION_HISTORY_QUERY_POLICY.staleTime,
        gcTime: ACTION_HISTORY_QUERY_POLICY.gcTime,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        initialPageParam: 1,
        getNextPageParam: (lastPage, pages) => (lastPage.has_more ? pages.length + 1 : undefined),
    })

    const quests = useMemo(
        () => result.data?.pages.flatMap(page => page.quests) || [],
        [result.data],
    )

    return {
        ...result,
        quests,
    }
}
